import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { HeaderService } from '../../core/services/header.service';
import { filter, Subscription } from 'rxjs';
import { MenuGroupEnum } from '../../core/security/menu-group.enum';
import { ZendeskService } from '../../core/services/zendesk.service';
import { ConfirmBecomeBetaDialogComponent } from './confirm-become-beta/confirm-become-beta-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatConfirmDialogComponent } from '../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { AccountService as ApiAccountService } from '../../api/services/account.service';
import { AuthService } from '../../core/security/auth.service';
import { ToggleBetaResponse } from '../../api/models/toggle-beta-response';
import { MatAlertDialogComponent } from '../../dialogs/mat-alert/mat-alert-dialog.component';
import { ReferralComponent } from '../referral/referral.component';
import { MatToolbar } from '@angular/material/toolbar';
import { FlexModule } from '@angular/flex-layout';
import { NgClass, NgIf } from '@angular/common';
import { MatIconButton, MatButton, MatIconAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { AppsPopupComponent } from './apps-popup.component';
import { NotificationsComponent } from '../notification/notifications.component';
import { CurrentUserComponent } from '../current-user/current-user.component';

@Component({
  selector: 'app-new-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatToolbar,
    FlexModule,
    NgClass,
    NgIf,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    RouterLink,
    AppsPopupComponent,
    MatIconAnchor,
    NotificationsComponent,
    CurrentUserComponent,
  ],
})
export class HeaderComponent implements OnInit, OnChanges, OnDestroy {
  static successPublicConfirmation = `${HeaderComponent.name}_successPublicConfirmation`;
  static successBetaConfirmation = `${HeaderComponent.name}_successBetaConfirmation`;

  public isMobileView = false;
  public isBetaCustomer = false;
  public hasPermissionForBeta = false;
  public settingsUrl = '';
  public title = '';

  private readonly _subscriptions = new Subscription();

  constructor(
    public headerService: HeaderService,
    public zendeskService: ZendeskService,
    public dialog: MatDialog,
    private authService: AuthService,
    private apiAccountService: ApiAccountService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this._subscriptions.add(this.subscribeMobileView());
    this._subscriptions.add(this.subscribeCurrentUser());
    this._subscriptions.add(this.subscribeHeaderTitle());

    this.settingsUrl = this.headerService.getAppDefaultUrlByGroup(MenuGroupEnum.SettingsGroup);

    this.showBetaToggleConfirmation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges', changes);
  }

  onGoBack(event) {
    this.headerService.goBack();
  }

  onReferBusiness(event) {
    this.dialog.open(ReferralComponent, {
      width: '500px',
      height: '90%',
    });
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  toggleHelpscout() {
    this.zendeskService.toggle();
  }

  onBetaClick($event: MouseEvent) {
    const dialogRef = this.dialog.open(ConfirmBecomeBetaDialogComponent, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const dialogConfirmRef = this.dialog.open(MatConfirmDialogComponent, {
          // width: '250px',
          // height: '90%',
          data: {
            title: 'Confirm',
            actionTitle: 'Yes',
            text: 'Are you sure you’d like to become a Beta tester?',
            hideCancel: false,
          },
        });

        dialogConfirmRef.afterClosed().subscribe(async confirmResult => {
          if (confirmResult) {
            const currentUser = this.authService.currentUser.getValue();
            localStorage.setItem(
              `${HeaderComponent.successBetaConfirmation}_${currentUser.customerProfileId}`,
              'showConfirmation',
            );
            await this.toggleBeta();
          }
        });
      }
    });
  }

  onStableClick($event: MouseEvent) {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '500px',
      data: {
        title: 'Switch Back to Public Version?',
        actionTitle: 'Switch Back to Public Version',
        text: 'Would you like to switch back to the Public version of the TeleGo customer portal? Please note that you may lose access to certain beta features. This change will apply to all users on your account.',
        hideCancel: false,
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const dialogConfirmRef = this.dialog.open(MatConfirmDialogComponent, {
          // width: '250px',
          // height: '90%',
          data: {
            title: 'Confirm',
            actionTitle: 'Yes',
            text: 'Are you sure you’d like to switch back to the pubic version?',
            hideCancel: false,
          },
        });

        dialogConfirmRef.afterClosed().subscribe(async confirmResult => {
          if (confirmResult) {
            const currentUser = this.authService.currentUser.getValue();
            localStorage.setItem(
              `${HeaderComponent.successPublicConfirmation}_${currentUser.customerProfileId}`,
              'showConfirmation',
            );
            await this.toggleBeta();
          }
        });
      }
    });
  }

  async toggleBeta(): Promise<ToggleBetaResponse> {
    const currentUser = this.authService.currentUser.getValue();
    if (currentUser) {
      const response = await this.apiAccountService
        .accountSettingsToggleBeta({
          body: {
            becomeBeta: !currentUser.isBetaCustomer,
          },
        })
        .toPromise();

      return response;
    }

    return null;
  }

  showBetaToggleConfirmation() {
    const currentUser = this.authService.currentUser.getValue();
    const showPublicConfirmation = localStorage.getItem(
      `${HeaderComponent.successPublicConfirmation}_${currentUser.customerProfileId}`,
    );
    const showBetaConfirmation = localStorage.getItem(
      `${HeaderComponent.successBetaConfirmation}_${currentUser.customerProfileId}`,
    );

    if (showPublicConfirmation) {
      localStorage.removeItem(`${HeaderComponent.successPublicConfirmation}_${currentUser.customerProfileId}`);
      this.dialog.open(MatAlertDialogComponent, {
        width: '500px',
        data: {
          title: 'Congratulations',
          actionTitle: 'Got It',
          text: 'You have successfully switched your TeleGo Customer Portal account to the public version.',
          hideCancel: false,
        },
      });
    }

    if (showBetaConfirmation) {
      localStorage.removeItem(`${HeaderComponent.successBetaConfirmation}_${currentUser.customerProfileId}`);
      this.dialog.open(MatAlertDialogComponent, {
        width: '500px',
        data: {
          title: 'Congratulations',
          actionTitle: 'Got It',
          text: 'Thank you requesting to be a TeleGo Customer Portal Beta Tester! We hope that you find value in upcoming features, and can’t wait to hear your feedback!',
          hideCancel: false,
        },
      });
    }
  }

  private subscribeHeaderTitle(): Subscription {
    return this.headerService.title$.pipe(filter(title => !!title)).subscribe(title => {
      this.title = title;
      this.cdr.detectChanges();
    });
  }

  private subscribeMobileView(): Subscription {
    return this.headerService.isMobileView.subscribe(value => {
      this.isMobileView = value;
      this.cdr.markForCheck();
    });
  }

  private subscribeCurrentUser(): Subscription {
    return this.authService.currentUser.subscribe(value => {
      this.isBetaCustomer = value?.isBetaCustomer || false;
      this.hasPermissionForBeta = value?.isAdmin || false;
    });
  }
}
